import Seo from "@/components/Seo";
import { FadeUp, Overline, GoldRule } from "@/components/Reveal";
import { TerraButton } from "@/components/Buttons";
import ReviewStrip from "@/components/ReviewStrip";
import { IMG } from "@/constants/site";

const LIST = [
  "Private dinner parties",
  "Corporate team dinners and client events",
  "Birthday celebrations and milestone dinners",
  "Cocktail-style gatherings and grazing events",
];

const STEPS = [
  { n: "01", text: "You reach out. We talk about your event, your guests, and what you are envisioning." },
  { n: "02", text: "We build a custom menu and a proposal." },
  { n: "03", text: "You say yes and we take it from there." },
];

export default function Catering() {
  return (
    <div>
      <Seo
        title="Catering — Sita · Small, Intimate Events up to 30 Guests"
        description="Sita caters small, intimate events for up to 30 people in Jersey City and NYC. No buffets, no generic platters — curated menus built around your event and your guests."
      />

      {/* ---------- Intro ---------- */}
      <section data-testid="catering-intro" className="mx-auto max-w-7xl px-6 pb-14 pt-14 md:px-10 md:pb-20 md:pt-20">
        <div className="max-w-3xl">
          <FadeUp>
            <Overline>Up to 30 guests · Jersey City & NYC</Overline>
            <h1 className="mt-4 font-display text-5xl font-light text-forest md:text-6xl">Catering</h1>
            <GoldRule className="mt-8" />
            <p className="mt-8 text-lg leading-relaxed text-ink/85">
              Sita caters small, intimate events for up to 30 people. We do not do buffets or generic platters. We do
              curated menus built around your event, your guests, and the experience you want to create.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ---------- Full-bleed image ---------- */}
      <section className="relative h-[46vh] overflow-hidden md:h-[62vh]" aria-hidden="true">
        <img src={IMG.cateringHero} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-deepforest/20" />
      </section>

      {/* ---------- What we cater ---------- */}
      <section data-testid="catering-what-we-cater" className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <Overline>What we cater</Overline>
              <h2 className="mt-4 font-display text-4xl font-light text-forest md:text-5xl">Gatherings we love to cook for</h2>
            </FadeUp>
            <ul className="mt-10 divide-y divide-forest/10 border-y border-forest/10">
              {LIST.map((item, i) => (
                <FadeUp key={item} delay={i * 0.08}>
                  <li className="flex items-center gap-6 py-6" data-testid={`catering-list-item-${i + 1}`}>
                    <span className="font-display text-sm italic text-gold">0{i + 1}</span>
                    <span className="font-display text-2xl text-forest md:text-3xl">{item}</span>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-5">
            <div className="overflow-hidden rounded-3xl">
              <img src={IMG.cateringSide} alt="A carefully plated Sita dish" className="aspect-[3/4] w-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------- From recent menus ---------- */}
      <section data-testid="catering-recent-menus" className="mx-auto max-w-7xl px-6 pb-20 md:px-10 md:pb-28">
        <FadeUp>
          <Overline>From our kitchen</Overline>
          <h2 className="mt-4 max-w-xl font-display text-4xl font-light text-forest md:text-5xl">Recent menus</h2>
        </FadeUp>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[IMG.menuDosa, IMG.menuNoodles, IMG.menuFarro].map((src, i) => (
            <FadeUp key={src} delay={i * 0.12}>
              <div className="group overflow-hidden rounded-3xl" data-testid={`catering-menu-dish-${i + 1}`}>
                <img
                  src={src}
                  alt="A Sita dish from a recent catering menu"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ---------- How it works ---------- */}
      <section data-testid="catering-how-it-works" className="bg-forest text-cream">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <FadeUp>
            <Overline light>How it works</Overline>
            <h2 className="mt-4 max-w-xl font-display text-4xl font-light md:text-5xl">From hello to held date</h2>
          </FadeUp>
          <div className="mt-14 grid gap-10 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.12} className="h-full">
                <div className="flex h-full flex-col border-t border-cream/20 pt-6">
                  <span className="font-display text-5xl font-light italic text-gold">{s.n}</span>
                  <p className="mt-5 flex-1 leading-relaxed text-cream/85">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <div className="mt-14">
              <TerraButton to="/inquiries" testid="catering-start-conversation-btn">
                Start the conversation <span aria-hidden="true">→</span>
              </TerraButton>
            </div>
          </FadeUp>
        </div>
      </section>

      <ReviewStrip />
    </div>
  );
}
